/**
 * Design tokens — the exact values from the design handoff
 * (`docs/design/design_handoff_dink_admin` → README "Design Tokens").
 *
 * These are the single source of truth for colors/maps the MUI theme cannot
 * express per-value (status/tier chips, avatar tints, the mono font stack).
 * Do NOT invent or tweak values here — they mirror the handoff verbatim.
 *
 * **Mode-aware semantic maps (DNKADM-0269).** The light-only maps below
 * (`STATUS`, `TIER_CHIP`, `AVATARS`, `GREEN_TINTS`) keep their exact light
 * values (the 0266 identity constraint) and gain dark counterparts, resolved
 * per mode through {@link buildSemanticMaps} / {@link resolveMaps}. The
 * CONSUMER PATTERN for feature surfaces: `const maps = resolveMaps(useTheme())`
 * then read `maps.status.active.bg`, `chipSx(maps.tierChip.junior)`,
 * `avatarFor(id, theme.palette.mode)` — never the raw light consts in `sx`.
 * Since DNKADM-0293 that ban is ENFORCED by lint, not just documented: importing
 * `GREEN_TINTS`/`STATUS`/`TIER_CHIP`/`AVATARS` anywhere in `src/**` outside
 * `src/theme/**` is an ESLint `no-restricted-imports` error. Raw color
 * literals in `src/features/**` are banned by the DNKADM-0269 ESLint guard
 * (`src/theme/**` owns the literals); domain-specific pair maps live in
 * `src/theme/maps/<domain>.ts`.
 */
import type { Theme } from '@mui/material/styles';

// ---- Brand palette (court green) ----
export const COURT_GREEN = '#2E7D52';
export const COURT_GREEN_DARK = '#266B46';
// Light page background — part of the DNKADM-0297 OKLCH surface ladder (see
// `buildTokens` in ./theme.ts for the full ladder + derivation and hex
// conversion notes), no longer the handoff's `#F4F2EE`. Same effective
// lightness, hue-155 tint, shipped as exact sRGB hex (MUI rejects oklch()).
export const PAGE_BG = '#EEF2EF'; // oklch(0.958 0.006 155)

// Reusable green tints (active nav bg, alert surfaces, etc.).
export const GREEN_TINTS = {
  navActive: '#E8F0EA',
  chip: '#E5EFE9',
  surfaceA: '#F3F8F4',
  surfaceB: '#F0F5F1',
} as const;

/**
 * Membership-status palette (chips, dots, ledger deltas). Exposed as a named
 * map because MUI's Chip can't theme per-value colors — components read e.g.
 * `STATUS.past_due.bg` directly.
 */
export const STATUS = {
  active: { label: 'Active', fg: '#1F6B43', bg: '#E5F1E9', dot: '#2E9D5E' },
  past_due: { label: 'Past due', fg: '#8A5A12', bg: '#FBEBD3', dot: '#D89A2E' },
  paused: { label: 'Paused', fg: '#5B5470', bg: '#ECEAF3', dot: '#8B82A8' },
  cancelled: { label: 'Cancelled', fg: '#9A4128', bg: '#F4E3DE', dot: '#C0492E' },
} as const;

/** Membership-tier chip color pairs (the three named tiers from the handoff). */
export const TIER_CHIP = {
  junior: { label: 'Junior', fg: '#5A6E2E', bg: '#EEF3E7' },
  performance: { label: 'Performance', fg: '#2E7D52', bg: '#E5EFE9' },
  performance_plus: { label: 'Performance Plus', fg: '#256A6E', bg: '#E2EDEF' },
} as const;

/** Avatar tint/ink pairs — hash a member id into this list (see `avatarFor`). */
export const AVATARS = [
  { bg: '#E5EFE9', fg: '#2E7D52' },
  { bg: '#E2EDEF', fg: '#256A6E' },
  { bg: '#F1E8DA', fg: '#8A5A12' },
  { bg: '#ECEAF3', fg: '#5B5470' },
  { bg: '#FBE7E0', fg: '#A84A2E' },
  { bg: '#E7EEF6', fg: '#33567E' },
] as const;

/**
 * Monospace stack for numbers, IDs, credit balances, bucket keys.
 * `JetBrains Mono` (DNKADM-0299 U2, replacing Roboto Mono) is self-hosted via
 * `@fontsource/jetbrains-mono` (imported in `fonts.ts`). A true monospace, so
 * numerals are tabular by construction — no `font-variant-numeric` needed.
 */
export const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * UI/body font stack — `Instrument Sans` (DNKADM-0299 U2, replacing Roboto),
 * self-hosted via `@fontsource/instrument-sans`.
 */
export const SANS = "'Instrument Sans', system-ui, Arial, sans-serif";

/**
 * Material Symbols Outlined font-family. Provided by
 * `@fontsource-variable/material-symbols-outlined`, whose `@font-face` declares
 * this exact family name. Referenced by the `<Icon>` glyph wrapper.
 */
export const MATERIAL_SYMBOLS_FONT = "'Material Symbols Outlined Variable'";

/**
 * Corner-radius vocabulary (DNKADM-0299 U3) — the approved design's radii as
 * NAMED tokens instead of literals scattered across components: small elements
 * tighter, cards and sheets softer. `theme.ts` wires every component override
 * from these. Note `theme.shape.borderRadius` stays 8 (= `control`) because it
 * is also the MULTIPLIER BASE for every numeric `sx.borderRadius` in the app —
 * changing it would rescale feature-level radii wholesale, which is a layout
 * change and out of scope.
 */
export const RADII = {
  /** Tiny letter tags (the design's 4px MEMBER/GUEST-class tags). */
  tag: 4,
  /** Inputs, toggle groups — and the `shape.borderRadius` base. */
  control: 8,
  /** Buttons. */
  button: 10,
  /** List rows, alerts, small inline panels. */
  row: 12,
  /** Cards / Paper surfaces. */
  card: 14,
  /** Dialogs and sheets — the largest surfaces. */
  sheet: 16,
  /** Capsule chips/badges (full round at any height). */
  pill: 999,
} as const;

// ---- Shadows (card / dialog / snackbar elevations from the handoff) ----
export const SHADOWS = {
  card: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
  dialog:
    '0 11px 15px -7px rgba(0,0,0,0.2), 0 24px 38px 3px rgba(0,0,0,0.14)',
  snackbar: '0 6px 16px rgba(0,0,0,0.3)',
} as const;

// ---- Mode-aware semantic maps (DNKADM-0269) -----------------------------------
// Light values above stay byte-identical (0266 identity constraint); the dark
// counterparts mirror the member app's translucent-tint + lifted-ink dark ramp
// (the same families as the dark `AdminTokens` in `theme.ts`).

/** One status entry (widened — the light consts are literal-typed). */
interface StatusStyle {
  label: string;
  fg: string;
  bg: string;
  dot: string;
}
/** One labeled chip pair (widened). */
interface ChipStyle {
  label: string;
  fg: string;
  bg: string;
}
/** One avatar tint/ink pair (widened). */
export interface AvatarPair {
  bg: string;
  fg: string;
}

const STATUS_DARK: Record<keyof typeof STATUS, StatusStyle> = {
  active: { label: 'Active', fg: '#7FD3A3', bg: 'rgba(46,157,94,0.18)', dot: '#2E9D5E' },
  past_due: { label: 'Past due', fg: '#E8C27A', bg: 'rgba(216,154,46,0.16)', dot: '#D89A2E' },
  paused: { label: 'Paused', fg: '#B3ABCB', bg: 'rgba(139,130,168,0.2)', dot: '#8B82A8' },
  cancelled: { label: 'Cancelled', fg: '#F0A090', bg: 'rgba(192,73,46,0.18)', dot: '#C0492E' },
};

const TIER_CHIP_DARK: Record<keyof typeof TIER_CHIP, ChipStyle> = {
  junior: { label: 'Junior', fg: '#C9D6A0', bg: 'rgba(122,148,62,0.2)' },
  performance: { label: 'Performance', fg: '#7FD3A3', bg: 'rgba(46,125,82,0.24)' },
  performance_plus: { label: 'Performance Plus', fg: '#8FD0D4', bg: 'rgba(37,106,110,0.26)' },
};

const AVATARS_DARK: readonly AvatarPair[] = [
  { bg: 'rgba(46,125,82,0.28)', fg: '#7FD3A3' },
  { bg: 'rgba(37,106,110,0.28)', fg: '#8FD0D4' },
  { bg: 'rgba(138,90,18,0.28)', fg: '#E8C27A' },
  { bg: 'rgba(91,84,112,0.32)', fg: '#B3ABCB' },
  { bg: 'rgba(168,74,46,0.28)', fg: '#F0A090' },
  { bg: 'rgba(51,86,126,0.32)', fg: '#9FC0E8' },
];

const GREEN_TINTS_DARK: Record<keyof typeof GREEN_TINTS, string> = {
  navActive: 'rgba(46,125,82,0.28)',
  chip: 'rgba(46,125,82,0.38)',
  surfaceA: 'rgba(46,125,82,0.1)',
  surfaceB: 'rgba(46,125,82,0.14)',
};

/** The mode-resolved bundle of the shared semantic maps (DNKADM-0269). */
export interface SemanticMaps {
  status: Record<keyof typeof STATUS, StatusStyle>;
  tierChip: Record<keyof typeof TIER_CHIP, ChipStyle>;
  avatars: readonly AvatarPair[];
  greenTints: Record<keyof typeof GREEN_TINTS, string>;
}

/** Per-mode semantic maps. Light === the exact handoff consts above. */
export function buildSemanticMaps(mode: 'light' | 'dark'): SemanticMaps {
  return mode === 'dark'
    ? { status: STATUS_DARK, tierChip: TIER_CHIP_DARK, avatars: AVATARS_DARK, greenTints: GREEN_TINTS_DARK }
    : { status: STATUS, tierChip: TIER_CHIP, avatars: AVATARS, greenTints: GREEN_TINTS };
}

/**
 * Resolve the semantic maps off the mounted theme — the feature-surface
 * consumer pattern (`const maps = resolveMaps(useTheme())`). Mirrors
 * `resolveTokens` in `theme.ts` (mode is read from `palette.mode`, so a bare
 * `createTheme()` in a test resolves light).
 */
export function resolveMaps(theme: Theme): SemanticMaps {
  return buildSemanticMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
}

// ---- Shared color helpers ----

/** Build the `sx` for a status/tier chip (Chip can't theme per-value colors). */
export const chipSx = (c: { fg: string; bg: string }) => ({
  bgcolor: c.bg,
  color: c.fg,
  fontWeight: 500,
});

/**
 * Hash a member id → an avatar tint pair. Mode-aware since DNKADM-0269 —
 * pass `theme.palette.mode`; the default keeps light (and every pre-0269
 * call site / bare-theme test) byte-identical.
 */
export function avatarFor(id: string, mode: 'light' | 'dark' = 'light'): AvatarPair {
  const list: readonly AvatarPair[] = mode === 'dark' ? AVATARS_DARK : AVATARS;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  // Both lists are non-empty, so the modulo index is always present.
  return list[Math.abs(h) % list.length] as AvatarPair;
}

/** Two-letter initials from a display name (for avatar circles). */
export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
